import React from 'react';
import './button.less';
import {
  getButtonClassName,
  getButtonHandler,
  getButtonLabel,
  BUTTON_VARIANTS,
  BUTTON_SIZES,
  getButtonVariant,
} from './core/button-runtime.js';

export type ButtonSize =
  | 'small'
  | 'medium'
  | 'large'
  | 'full-width'
  | 'icon-only'
  // Allow the runtime's looser aliases (e.g. 'fullwidth', 'icon') without losing autocomplete.
  | (string & {});

export type ButtonIconPosition = 'left' | 'right' | 'only' | (string & {});

/**
 * Props for {@link Button}. Extends the intrinsic element attributes so any
 * standard DOM prop (id, data-*, aria-*, event handlers) passes through.
 */
export interface ButtonProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'style' | 'color'> {
  /** Visible label (alternative to `children`); usually text but may be a node. */
  label?: React.ReactNode;
  children?: React.ReactNode;
  /** React Native parity handler; treated as an alias of `onClick`. */
  onPress?: React.MouseEventHandler<HTMLElement>;
  onClick?: React.MouseEventHandler<HTMLElement>;
  primary?: boolean;
  secondary?: boolean;
  tertiary?: boolean;
  error?: boolean;
  grey?: boolean;
  size?: ButtonSize;
  /** Explicit variant name; overrides the boolean flags. */
  variant?: string;
  /** Explicit color/variant alias. */
  color?: string;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  loading?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  iconPosition?: ButtonIconPosition;
  style?: React.CSSProperties;
  textStyle?: React.CSSProperties;
  /** Render as a different element/component (polymorphic). */
  as?: React.ElementType;
  to?: string;
  href?: string;
  /** Alias of `as`. */
  component?: React.ElementType;
  sx?: React.CSSProperties;
  accessibilityLabel?: string;
  a11yLabel?: string;
  ariaLabel?: string;
  /** Confirm before firing when the resolved variant is destructive. */
  destructiveConfirm?: boolean;
  destructiveConfirmMessage?: string;
}

const CLEAN_VARIANT = (value: unknown): string =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

const CLEAN_ICON_POSITION = (value: unknown): 'left' | 'right' | 'only' => {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (normalized === 'right' || normalized === 'trailing') return 'right';
  if (normalized === 'only' || normalized === 'icon' || normalized === 'icononly' || normalized === 'icon-only') return 'only';
  return 'left';
};

const resolveIconPlacement = ({
  startIcon,
  endIcon,
  iconPosition = 'left',
}: {
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  iconPosition?: 'left' | 'right' | 'only';
}): { startIcon: React.ReactNode; endIcon: React.ReactNode } => {
  if (iconPosition === 'only') {
    return {
      startIcon: startIcon || endIcon,
      endIcon: null,
    };
  }

  if (iconPosition === 'right') {
    return {
      startIcon: endIcon ? startIcon : null,
      endIcon: endIcon || startIcon,
    };
  }

  return { startIcon, endIcon };
};

export const Button = ({
  label = '',
  children,
  onPress,
  onClick,
  primary = false,
  secondary = false,
  tertiary = false,
  error = false,
  grey = false,
  size = 'medium',
  variant,
  color,
  className = '',
  type = 'button',
  disabled = false,
  loading = false,
  startIcon,
  endIcon,
  iconPosition = 'left',
  style,
  textStyle,
  as: Element,
  to,
  href,
  component,
  sx,
  accessibilityLabel,
  a11yLabel,
  ariaLabel,
  destructiveConfirm = true,
  destructiveConfirmMessage = 'This is a destructive action. This cannot be undone. Continue?',
  ...props
}: ButtonProps) => {
  const resolvedVariant = getButtonVariant({
    primary,
    secondary,
    tertiary,
    error,
    grey,
    variant: CLEAN_VARIANT(variant),
    color: CLEAN_VARIANT(color),
  });

  const normalizedSize = BUTTON_SIZES && size != null
    ? `${size}`.trim().toLowerCase()
    : BUTTON_SIZES.MEDIUM;

  const isIconOnlyRequested = normalizedSize === 'icon-only' || normalizedSize === 'icononly' || normalizedSize === 'icon';
  const isFullWidthRequested = normalizedSize === 'full-width' || normalizedSize === 'fullwidth' || normalizedSize === 'full';
  const normalizedIconPosition = CLEAN_ICON_POSITION(iconPosition);
  const isExplicitIconOnly = normalizedIconPosition === 'only';
  const resolvedSize = isFullWidthRequested
    ? 'full-width'
    : isIconOnlyRequested || isExplicitIconOnly
      ? 'icon-only'
      : normalizedSize;

  const classes = getButtonClassName({
    variant: resolvedVariant,
    size: resolvedSize,
    className,
    loading,
  });

  const isLoading = Boolean(loading);
  const effectiveLabel = label ?? '';
  // Assign to a variable first so structural extra props (children) are accepted
  // by the loosely-inferred JS helper signature.
  const labelArgs = { children, label: effectiveLabel };
  const content = getButtonLabel(labelArgs as { label?: string; children?: React.ReactNode });
  const sizeBasedStyle: React.CSSProperties = {
    ...(isFullWidthRequested ? { width: '100%' } : {}),
    ...(isIconOnlyRequested || isExplicitIconOnly
      ? {
          width: style?.width ?? 'var(--ls-button-icon-size, 44px)',
          minWidth: style?.minWidth ?? style?.width ?? 'var(--ls-button-icon-size, 44px)',
          height: style?.height,
          minHeight: style?.minHeight,
          justifySelf: 'stretch',
        }
      : {}),
  };
  const normalizedStyle: React.CSSProperties = {
    ...style,
    ...sizeBasedStyle,
  };

  const hasExplicitLabel = (typeof label === 'string' || typeof label === 'number')
    && String(label).trim().length > 0;
  const hasExplicitChildLabel =
    (typeof children === 'string' || typeof children === 'number') && String(children).trim().length > 0;
  const hasLabelContent = React.Children.toArray(content).some((child) => (
    typeof child === 'string'
      ? child.trim().length > 0
      : typeof child === 'number' || React.isValidElement(child)
  ));
  const isIconOnly = isIconOnlyRequested
    || isExplicitIconOnly
    || (!hasLabelContent && (startIcon || endIcon));
  const { startIcon: resolvedStartIcon, endIcon: resolvedEndIcon } = resolveIconPlacement({
    startIcon,
    endIcon,
    iconPosition: normalizedIconPosition,
  });
  const explicitTextLabel = hasExplicitLabel
    ? String(label).trim()
    : hasExplicitChildLabel
      ? String(children).trim()
      : '';
  const loadingText = 'Loading ...';
  const visualContent = isLoading ? loadingText : content;
  const finalTag: React.ElementType = Element || component || (to || href ? 'a' : 'button');
  const isLink = finalTag === 'a';
  const isDisabled = Boolean(disabled || loading);
  const htmlAriaLabel = props['aria-label'];
  const readableLabel = accessibilityLabel || a11yLabel || ariaLabel || htmlAriaLabel;
  const ariaLabelValue = readableLabel || (
    isLoading
      ? `${explicitTextLabel || 'Button'} ${loadingText}`.trim()
      // Let the browser name rich/fragment labels from their rendered content.
      // A generic aria-label would hide that text from assistive technology.
      : explicitTextLabel || (hasLabelContent && !isIconOnly ? undefined : 'Button')
  );
  const isDestructive = destructiveConfirm && resolvedVariant === BUTTON_VARIANTS.ERROR;
  const defaultOnClick = getButtonHandler({ onClick, onPress });
  const hasActivationHandler = typeof defaultOnClick === 'function';
  if (
    process.env.NODE_ENV !== 'production' &&
    isIconOnly &&
    !readableLabel
  ) {
    console.warn(
      'Icon-only button should provide accessibilityLabel/a11yLabel/ariaLabel for screen readers.',
    );
  }
  const onActivate = (event: React.SyntheticEvent) => {
    if (isDisabled) {
      return;
    }
    if (!hasActivationHandler) {
      return;
    }
    if (!isDestructive) {
      return defaultOnClick(event);
    }
    const message = destructiveConfirmMessage || 'This is a destructive action. This cannot be undone. Continue?';
    if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
      if (!window.confirm(message)) {
        return;
      }
      return defaultOnClick(event);
    }
    return defaultOnClick(event);
  };
  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (
      (event?.key === 'Enter' || event?.key === ' ' || event?.key === 'Spacebar') &&
      isLink &&
      hasActivationHandler
    ) {
      event.preventDefault();
      onActivate(event);
    }

    if (typeof props.onKeyDown === 'function') {
      props.onKeyDown(event);
    }
  };

  const elementProps: Record<string, unknown> = {
    type: finalTag === 'button' ? type : undefined,
    disabled: finalTag === 'button' ? isDisabled : undefined,
    ...(isLink
      ? {
          role: 'button',
          tabIndex: isDisabled ? -1 : 0,
        }
      : {}),
    'aria-disabled': isDisabled ? 'true' : undefined,
    'aria-label': ariaLabelValue,
    'aria-busy': isLoading ? 'true' : undefined,
    'aria-live': isLoading ? 'polite' : undefined,
    href: finalTag === 'a' ? href || to : undefined,
    className: classes,
    style: normalizedStyle,
    ...props,
    onClick: isDisabled ? undefined : hasActivationHandler ? onActivate : undefined,
    onKeyDown: isLink ? onKeyDown : props.onKeyDown,
  };

  return React.createElement(
    finalTag,
    elementProps,
    <span className="ls-button__content" style={textStyle}>
      {resolvedStartIcon ? <span className="ls-button__icon ls-button__icon--start">{resolvedStartIcon}</span> : null}
      {visualContent}
      {resolvedEndIcon ? <span className="ls-button__icon ls-button__icon--end">{resolvedEndIcon}</span> : null}
    </span>
  );
};

export default Button;
